// C:\Users\server\Desktop\FRONTEND\app-user-front\src\pages\Dashboard\ProductDetails.js
import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Container, Row, Col, Button, Spinner, Alert, Card, Badge } from "react-bootstrap";
import axios from "axios";
import RequestModal from "./requestmodel";
import CONFIG from "../../config";

// ------------------- Helper: API origin + getImageUrl -------------------
const API_ORIGIN = (window.APP_CONFIG && window.APP_CONFIG.API_URL ? window.APP_CONFIG.API_URL.replace(/\/$/, "") : (CONFIG.API_URL || "").replace(/\/$/, ""));

function getImageUrl(input) {
  if (!input) return null;
  let img = null;
  if (typeof input === "object" && input.image) img = input.image;
  else if (typeof input === "string") img = input;
  if (!img || typeof img !== "string") return null;
  const trimmed = img.trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (trimmed.startsWith("/")) return `${API_ORIGIN}${trimmed}`;
  return `${API_ORIGIN}/${trimmed}`;
}

// ------------------- Lightbox Component -------------------
// Fullscreen white background, 0 radius, zoom & pan.
const Lightbox = ({ show, src, alt, onClose }) => {
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (show) {
      document.body.style.overflow = "hidden";
      // reset transform when open
      setScale(1);
      setTranslate({ x: 0, y: 0 });
    } else {
      document.body.style.overflow = "auto";
    }
    return () => (document.body.style.overflow = "auto");
  }, [show]);

  useEffect(() => {
    const onKey = (e) => {
      if (!show) return;
      if (e.key === "Escape") onClose();
      if (e.key === "+" || e.key === "=") setScale((s) => Math.min(4, +(s + 0.25).toFixed(2)));
      if (e.key === "-") setScale((s) => Math.max(0.25, +(s - 0.25).toFixed(2)));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [show, onClose]);

  const onWheel = (e) => {
    if (!show) return;
    e.preventDefault();
    const delta = e.deltaY;
    // zoom towards mouse position (approx)
    setScale((s) => {
      const next = delta > 0 ? s * 0.9 : s * 1.1;
      return +Math.max(0.25, Math.min(6, next)).toFixed(3);
    });
  };

  const pointerDown = (e) => {
    if (!show) return;
    setIsPanning(true);
    panStartRef.current = { x: e.clientX, y: e.clientY, origX: translate.x, origY: translate.y };
    (e.target || e.srcElement).setPointerCapture && (e.target || e.srcElement).setPointerCapture(e.pointerId);
  };
  const pointerMove = (e) => {
    if (!isPanning || !panStartRef.current) return;
    const dx = e.clientX - panStartRef.current.x;
    const dy = e.clientY - panStartRef.current.y;
    setTranslate({ x: panStartRef.current.origX + dx, y: panStartRef.current.origY + dy });
  };
  const pointerUp = (e) => {
    setIsPanning(false);
    panStartRef.current = null;
  };

  const zoomIn = () => setScale((s) => Math.min(6, +(s * 1.2).toFixed(3)));
  const zoomOut = () => setScale((s) => Math.max(0.25, +(s / 1.2).toFixed(3)));
  const reset = () => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
  };

  if (!show) return null;

  return (
    <div
      ref={containerRef}
      role="dialog"
      aria-modal="true"
      className="lightbox-overlay"
      onWheel={onWheel}
      style={{
        position: "fixed",
        inset: 0,
        background: "#ffffff",
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      }}
      onDoubleClick={reset}
      onPointerDown={pointerDown}
      onPointerMove={pointerMove}
      onPointerUp={pointerUp}
      onPointerCancel={pointerUp}
    >
      <button
        aria-label="Close"
        onClick={onClose}
        className="lightbox-close"
        style={{
          position: "fixed",
          top: 16,
          right: 16,
          zIndex: 2100,
          background: "#222",
          color: "#fff",
          border: "none",
          borderRadius: 6,
          padding: "8px 10px",
          cursor: "pointer",
        }}
      >
        ✕
      </button>

      <div
        style={{
          position: "fixed",
          top: 16,
          left: 16,
          zIndex: 2100,
          display: "flex",
          gap: 8,
        }}
      >
        <button onClick={zoomOut} aria-label="Zoom out" className="lightbox-zoom" style={controlBtnStyle}>
          −
        </button>
        <button onClick={reset} aria-label="Reset" className="lightbox-reset" style={controlBtnStyle}>
          ⤾
        </button>
        <button onClick={zoomIn} aria-label="Zoom in" className="lightbox-zoom" style={controlBtnStyle}>
          +
        </button>
      </div>

      <img
        src={src}
        alt={alt}
        draggable={false}
        style={{
          transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
          transition: isPanning ? "none" : "transform 0.12s ease-out",
          maxWidth: "none", // allow image larger than viewport for zoom/pan
          maxHeight: "none",
          width: "auto",
          height: "auto",
          objectFit: "contain",
          // keep no border radius
          borderRadius: 0,
          userSelect: "none",
          cursor: isPanning ? "grabbing" : scale > 1 ? "grab" : "zoom-in",
          boxShadow: "0 0 0 rgba(0,0,0,0)",
        }}
      />
    </div>
  );
};

const controlBtnStyle = {
  background: "#222",
  color: "#fff",
  border: "none",
  borderRadius: 6,
  padding: "8px 10px",
  cursor: "pointer",
  fontSize: 16,
};

// ------------------- ProductDetails Component -------------------
const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [mainImage, setMainImage] = useState("");
  const [similarProducts, setSimilarProducts] = useState([]);
  const [imageLoading, setImageLoading] = useState(true);
  const [requestModalOpen, setRequestModalOpen] = useState(false);

  // lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // keep a ref to the preloader so we can cleanup
  const imagePreloaderRef = useRef(null);

  // FORCE SCROLL TO TOP WHEN PAGE OPENS
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, [id]);

  useEffect(() => {
    const fetchProductDetails = async () => {
      try {
        setLoading(true);

        const response = await fetch(`${API_ORIGIN}/products`);
        if (!response.ok) throw new Error("Failed to fetch products");
        const result = await response.json();

        const productsArray = Array.isArray(result)
          ? result
          : Array.isArray(result.products)
          ? result.products
          : Array.isArray(result.data)
          ? result.data
          : [];

        if (!Array.isArray(productsArray)) {
          throw new Error("Invalid products data format");
        }

        const productData = productsArray.find((p) => Number(p.id) === Number(id));
        if (!productData) throw new Error(`Product with ID ${id} not found`);

        // Normalize product image field into absolute URL
        const normalizedProduct = {
          ...productData,
          image:
            productData.image && typeof productData.image === "string" ? getImageUrl(productData.image) : null,
        };

        setProduct(normalizedProduct);

        // Determine resolved main image URL (either product image or default)
        const resolvedMain = normalizedProduct.image || "/images/default-product.jpg";

        // cleanup previous preloader if any
        if (imagePreloaderRef.current) {
          imagePreloaderRef.current.onload = null;
          imagePreloaderRef.current.onerror = null;
        }

        setImageLoading(true);
        const img = new Image();
        imagePreloaderRef.current = img;

        img.onload = () => {
          setMainImage(resolvedMain);
          setImageLoading(false);
        };
        img.onerror = () => {
          setMainImage("/images/default-product.jpg");
          setImageLoading(false);
        };
        img.src = resolvedMain;

        // Prepare similar products (4) and normalize image URLs
        const sims = productsArray
          .filter((p) => Number(p.id) !== Number(id) && (p.category || "") === (productData.category || ""))
          .slice(0, 4)
          .map((p) => ({
            ...p,
            image: p.image && typeof p.image === "string" ? getImageUrl(p.image) : null,
          }));

        setSimilarProducts(sims);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching product:", err);
        setError(err.message || "Failed to load product");
        setLoading(false);
      }
    };

    fetchProductDetails();

    // cleanup function when component unmounts or id changes
    return () => {
      if (imagePreloaderRef.current) {
        imagePreloaderRef.current.onload = null;
        imagePreloaderRef.current.onerror = null;
        imagePreloaderRef.current = null;
      }
    };
  }, [id]);

  const handleMakeOfferClick = () => {
    setRequestModalOpen(true);
  };

  const toggleRequestModal = () => {
    setRequestModalOpen(!requestModalOpen);
  };

  const handleRequestSubmit = async (requesterEmail, productId) => {
    try {
      const response = await axios.post(`${API_ORIGIN}/swap-requests`, {
        requester_email: requesterEmail,
        product_id: productId,
      });

      if (response.data.message) {
        alert("✅ " + response.data.message);
      } else {
        alert("✅ Request sent successfully!");
      }
    } catch (error) {
      console.error("Error sending swap request:", error);

      if (error.response?.data?.message) {
        alert("❌ " + error.response.data.message);
      } else if (error.response?.data?.errors) {
        const errors = Object.values(error.response.data.errors)
          .flat()
          .join("\n");
        alert("❌ " + errors);
      } else {
        alert("❌ Failed to send request. Please try again.");
      }
      throw error;
    }
  };

  const handleImageLoad = () => {
    setImageLoading(false);
  };

  const handleImageError = () => {
    if (mainImage !== "/images/default-product.jpg") setMainImage("/images/default-product.jpg");
    setImageLoading(false);
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading product details...</p>
        </div>
      </Container>
    );
  }

  if (error || !product) {
    return (
      <Container style={{ marginTop: "40px" }}>
        <Alert variant="danger" className="mt-4">
          <h4>Product Not Found</h4>
          <p>{error || "The product you are looking for does not exist."}</p>
          <Button variant="primary" onClick={() => navigate("/products")}>
            Back to Products
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <>
      <section className="section" style={styles.section}>
        <Container>
          {/* Breadcrumb Navigation */}
          <nav aria-label="breadcrumb" className="mb-4">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/" className="text-decoration-none">
                  Home
                </Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/products" className="text-decoration-none">
                  Products
                </Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                {product.name}
              </li>
            </ol>
          </nav>

          <Row>
            {/* Product Image Section */}
            <Col lg={6} className="mb-4">
              <div className="product-image-container" style={styles.imageContainer}>
                {imageLoading && (
                  <div style={styles.imageLoading}>
                    <Spinner animation="border" />
                    <span>Loading image...</span>
                  </div>
                )}

                {/* clicking this opens the lightbox */}
                <img
                  src={mainImage}
                  alt={product.name}
                  className="product-main-image"
                  style={{
                    ...styles.mainImage,
                    opacity: imageLoading ? 0 : 1,
                    // no hover transforms anywhere
                    transform: "none",
                    cursor: "zoom-in",
                    borderRadius: 0,
                  }}
                  onLoad={handleImageLoad}
                  onError={handleImageError}
                  onClick={() => setLightboxOpen(true)}
                  draggable={false}
                />
              </div>
            </Col>

            {/* Product Information Section */}
            <Col lg={6}>
              <div className="product-info">
                {/* Category and Status */}
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <Badge bg="secondary" style={styles.categoryBadge}>
                    {product.category || "Uncategorized"}
                  </Badge>
                  <Badge bg="secondary" style={styles.statusBadge}>
                    <i className="ri-checkbox-circle-line me-1"></i>
                    Available for Swap
                  </Badge>
                </div>

                {/* Product Title */}
                <h1 className="product-title mb-3" style={styles.productTitle}>
                  {product.name}
                </h1>

                {/* Description */}
                <div className="description-section mb-4">
                  <h5 className="mb-3" style={styles.sectionTitle}>
                    Description
                  </h5>
                  <p className="text-muted" style={styles.description}>
                    {product.description || "No detailed description available for this product."}
                  </p>
                </div>

                {/* Product Specifications */}
                <div className="specifications-section mb-4">
                  <h5 className="mb-3" style={styles.sectionTitle}>
                    Product Details
                  </h5>
                  <div className="specs-grid" style={styles.specsGrid}>
                    <div className="spec-item" style={styles.specItem}>
                      <strong>Category:</strong>
                      <span>{product.category || "Not specified"}</span>
                    </div>
                    <div className="spec-item" style={styles.specItem}>
                      <strong>Product ID:</strong>
                      <span>#{product.id}</span>
                    </div>
                    <div className="spec-item" style={styles.specItem}>
                      <strong>Added On:</strong>
                      <span>{product.created_at ? new Date(product.created_at).toLocaleDateString() : "-"}</span>
                    </div>
                  </div>
                </div>

                {/* Seller Information */}
                <Card className="seller-section mb-4">
                  <Card.Body>
                    <h6 className="mb-3" style={styles.sectionTitle}>
                      Seller Information
                    </h6>
                    <div className="d-flex align-items-center">
                      <div
                        className="seller-avatar bg-secondary rounded-circle d-flex align-items-center justify-content-center me-3"
                        style={styles.avatar}
                      >
                        <span className="text-white fw-bold">{product.email ? product.email.charAt(0).toUpperCase() : "U"}</span>
                      </div>
                      <div>
                        <div className="fw-medium">{product.email || "Unknown Seller"}</div>
                        <small className="text-muted">
                          Member since {product.created_at ? new Date(product.created_at).getFullYear() : "2024"}
                        </small>
                      </div>
                    </div>
                  </Card.Body>
                </Card>

                {/* Action Buttons */}
                <div className="action-buttons mt-4">
                  <Row>
                    <Col sm={6} className="mb-3">
                      <Button variant="warning" size="lg" className="w-100 py-3" onClick={handleMakeOfferClick} style={styles.makeOfferBtn}>
                        <i className="ri-send-plane-line me-2"></i>
                        Make an Offer
                      </Button>
                    </Col>
                  </Row>
                  <div className="text-center mt-2">
                    <small className="text-muted">By making an offer, you agree to our terms and conditions</small>
                  </div>
                </div>
              </div>
            </Col>
          </Row>

          {/* Similar Products Section */}
          {similarProducts.length > 0 && (
            <Row className="mt-5">
              <Col lg={12}>
                <div className="similar-products-section">
                  <h4 className="mb-4" style={styles.sectionTitle}>
                    Similar Products
                  </h4>
                  <Row>
                    {similarProducts.map((similarProduct) => (
                      <Col key={similarProduct.id} xs={12} sm={6} md={3} className="mb-4">
                        <Card
                          className="h-100 product-card"
                          style={{
                            ...styles.productCard,
                          }}
                          onClick={() => navigate(`/product/${similarProduct.id}`)}
                        >
                          <div style={{ height: "200px", overflow: "hidden", position: "relative" }}>
                            <Card.Img
                              variant="top"
                              src={similarProduct.image || "/images/default-product.jpg"}
                              style={{
                                height: "100%",
                                objectFit: "cover",
                                transition: "transform 0.3s ease",
                              }}
                              onError={(e) => {
                                e.target.src = "/images/default-product.jpg";
                              }}
                            />
                          </div>
                          <Card.Body>
                            <Card.Title className="text-truncate" style={{ fontSize: "1rem" }}>
                              {similarProduct.name}
                            </Card.Title>
                            <Card.Text className="text-muted small">{similarProduct.category || "Uncategorized"}</Card.Text>
                          </Card.Body>
                          <Card.Footer>
                            <small className="text-dark">
                              <i className="ri-exchange-fill me-1"></i>
                              Available for Swap
                            </small>
                          </Card.Footer>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </div>
              </Col>
            </Row>
          )}

          {/* Back to Products Button */}
          <Row className="mt-4">
            <Col className="text-center">
              <Button variant="outline-secondary" onClick={() => navigate("/products")} size="lg">
                <i className="ri-arrow-left-line me-2"></i>
                Back to All Products
              </Button>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Lightbox */}
      <Lightbox show={lightboxOpen} src={mainImage || "/images/default-product.jpg"} alt={product.name || "Product image"} onClose={() => setLightboxOpen(false)} />

      {/* REQUEST MODAL */}
      <RequestModal show={requestModalOpen} onClose={toggleRequestModal} product={product} onRequestSubmit={handleRequestSubmit} />
    </>
  );
};

// Styles
const styles = {
  section: {
    padding: "40px 0",
    backgroundColor: "#ffffff", // always white
    minHeight: "100vh",
  },
  imageContainer: {
    borderRadius: "0px", // no radius
    overflow: "hidden",
    background: "#ffffff",
    boxShadow: "0 4px 20px rgba(0,0,0,0.03)",
    minHeight: "400px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
  },
  mainImage: {
    width: "100%",
    height: "400px",
    objectFit: "cover",
    transition: "opacity 0.2s ease",
    borderRadius: 0,
  },
  imageLoading: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "12px",
    color: "#6c757d",
  },
  categoryBadge: {
    backgroundColor: "#702963",
    color: "#fff",
    fontSize: "0.8rem",
    padding: "0.5rem 1rem",
  },

  statusBadge: {
    backgroundColor: "#FF7F50",
    color: "#fff",
    fontSize: "0.9rem",
    padding: "0.5rem 1rem",
  },

  productTitle: {
    fontSize: "2.5rem",
    fontWeight: "700",
    color: "#212529",
    lineHeight: "1.2",
  },
  sectionTitle: {
    color: "#495057",
    fontWeight: "600",
    borderBottom: "2px solid #818181ff",
    paddingBottom: "0.5rem",
    display: "inline-block",
  },
  description: {
    fontSize: "1.1rem",
    lineHeight: "1.7",
    textAlign: "justify",
  },
  specsGrid: {
    backgroundColor: "#fff",
    padding: "1.5rem",
    borderRadius: "12px",
    border: "1px solid #e9ecef",
    boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
    display: "grid",
    gap: "0.5rem",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  },
  specItem: {
    display: "flex",
    justifyContent: "space-between",
    padding: "0.5rem 0",
    borderBottom: "1px solid #f1f1f1",
  },
  avatar: {
    width: "50px",
    height: "50px",
    fontSize: "1.2rem",
  },
  makeOfferBtn: {
    background: "linear-gradient(#FF7F50, #FF7F50)",
    border: "none",
    fontWeight: "600",
    fontSize: "1.1rem",
  },
  productCard: {
    cursor: "pointer",
    transition: "transform 0.2s ease, boxShadow 0.2s ease",
    boxShadow: "0 0 0 rgba(0,0,0,0)",
  },
  productCardHover: {
    transform: "translateY(-5px)",
    boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
  },
};

export default ProductDetails;
